import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CSpinner,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CCol,
  CRow,
  CTabs,
  CTabList,
  CTab,
  CTabContent,
  CTabPanel,
  CFormSwitch,
  CCollapse,
} from "@coreui/react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import "highcharts/highcharts-more";

import { apiClient } from "services/Api";
import { jwtDecode } from "jwt-decode";
import DataTableComponent from "components/DataTableComponent";
import RecordDetailOffcanvas from "components/RecordDetailOffcanvas";
import ToastNotification from "components/globals/ToastNotification";

const typologyGroups = [
  {
    label: "Com incentivo",
    options: [
      { value: "with_incentive_energy", label: "Energia" },
      { value: "with_incentive_idt", label: "IDT" },
      { value: "with_incentive_inov", label: "Inovação" },
      { value: "with_incentive_inter", label: "Internacionalização" },
      { value: "with_incentive_others", label: "Outros" },
    ],
  },
  {
    label: "Sem incentivo",
    options: [
      { value: "without_incentive_consulting", label: "Consultoria" },
      { value: "without_incentive_studies", label: "Estudos" },
      { value: "without_incentive_others", label: "Outros" },
    ],
  },
];

const SatisfactionSurveyList = () => {
  const navigate = useNavigate();
  const [satisfactionSurveys, setSatisfactionSurveys] = useState([]);
  const location = useLocation();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [distributions, setDistributions] = useState(null);
  const [missingEmails, setMissingEmails] = useState(null);
  const [previewSurvey, setPreviewSurvey] = useState(null);
  const [activePolarTab, setActivePolarTab] = useState("with");
  const [selectedTypologies, setSelectedTypologies] = useState([]);
  const [matchAllTypologies, setMatchAllTypologies] = useState(false);
  const [onlyConcluded, setOnlyConcluded] = useState(false);
  const [commentsVisible, setCommentsVisible] = useState(false);

  const token = localStorage.getItem("access_token");
  const decodedToken = jwtDecode(token);
  const is_director = decodedToken.is_director;

  const buildSurveysUrl = () => {
    const params = new URLSearchParams({ year: selectedYear });
    if (selectedTypologies.length) {
      params.set("typologies", selectedTypologies.join(","));
      if (matchAllTypologies) {
        params.set("typology_match", "all");
      }
    }
    return `/satisfaction-surveys/?${params.toString()}`;
  };

  useEffect(() => {
    const fetchSatisfactionSurveys = async () => {
      try {
        const response = await apiClient.get(buildSurveysUrl());
        setSatisfactionSurveys(response.data.satisfactionSurveys);
        setAvailableYears(response.data.available_years);
        setKpis(response.data.kpis || null);
        setDistributions(response.data.distributions || null);
      } catch (error) {
        console.error("Error fetching satisfactionSurveys:", error);
      }
    };

    fetchSatisfactionSurveys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, selectedYear, selectedTypologies, matchAllTypologies]);

  const getClientName = (survey) =>
    survey.client ? survey.client_name : survey.old_client_name;

  const tableSurveys = onlyConcluded
    ? satisfactionSurveys.filter((survey) => survey.concluded)
    : satisfactionSurveys;
  const surveysWithComments = tableSurveys.filter((survey) =>
    survey.suggestions?.trim(),
  );

  const toggleTypology = (value) =>
    setSelectedTypologies((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value],
    );

  const handleSendEmails = async () => {
    const confirmed = window.confirm(
      "Confirmar envio de emails dos questionários de satisfação?"
    );
    if (!confirmed) {
      return;
    }
    setLoading(true);

    try {
      //return; // Temporarily disabled the email sending functionality
      const response = await apiClient.post(
        `/satisfaction-surveys/send-emails/`,
      );
      setToast({
        title: "Questionários de satisfação",
        message: "Questionários enviados com sucesso",
        type: "success",
      });
      if (response.data.missing_emails.length > 0) {
        setMissingEmails(response.data.missing_emails);
      }

      const updatedResponse = await apiClient.get(buildSurveysUrl());
      setSatisfactionSurveys(updatedResponse.data.satisfactionSurveys);
      setAvailableYears(updatedResponse.data.available_years);
      setKpis(updatedResponse.data.kpis || null);
      setDistributions(updatedResponse.data.distributions || null);
    } catch (error) {
      console.error("Error sending satisfaction surveys:", error);
      setToast({
        title: "Questionários de satisfação",
        message: "Erro ao enviar os questionários.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      name: "Cliente",
      selector: (row) => (row.client ? row.client_name : row.old_client_name),
      sortable: true,
      className: "text-center",
      grow: 2,
    },
    {
      name: "Preenchido",
      selector: (row) => (row.concluded ? "Sim" : "Não"),
      sortable: true,
      className: "text-center",
    },
    {
      name: "Última data de envio",
      selector: (row) => row.last_sent_date || "",
      sortable: true,
    },
    { name: "Qtd. envios", selector: (row) => row.times_sent, sortable: true },
    {
      name: "Enviado para",
      selector: (row) => row.sent_to || "",
      sortable: true,
      grow: 2,
    },
  ];

  const evaluationFields = [
    ["information_clarity", "Qualidade da informação disponibilizada"],
    ["question_resolution", "Esclarecimento de dúvidas"],
    ["response_time", "Tempo de resposta a solicitações"],
    ["issue_resolution", "Eficácia na resolução de problemas"],
    ["integration_capacity", "Integração de outros serviços"],
    ["condition_compliance", "Cumprimento das condições"],
    ["competence_level", "Competência e responsabilidade"],
    ["overall_evaluation", "Qualidade global do serviço"],
    ["ideal_supplier", "STREAM está próxima de ser o fornecedor ideal?"],
    ["quality_price_ratio", "Qualidade do serviço face ao preço"],
    ["error_frequency", "Houve aspetos que correram mal?"],
    ["problem_management", "Como foram geridos os problemas?"],
    ["project_delivery", "Voltaria a entregar projeto à STREAM?"],
    ["recommendation", "Recomendaria os serviços?"],
  ];

  const typologyColorMap = {
    Energia: "#2563eb",
    IDT: "#7c3aed",
    Inovação: "#0f766e",
    Internacionalização: "#f97316",
    Outros: "#64748b",
    Consultoria: "#0ea5e9",
    Estudos: "#22c55e",
  };

  const normalizeTypologyName = (name) =>
    name.replace(/^Com incentivo:\s*/i, "").replace(/^Sem incentivo:\s*/i, "");

  const buildPolarOptions = (series) => ({
    chart: {
      polar: true,
      type: "line",
      height: 360,
      spacing: [12, 12, 12, 12],
      backgroundColor: "transparent",
    },
    title: { text: null },
    credits: { enabled: false },
    exporting: { enabled: false },
    xAxis: {
      categories: evaluationFields.map(([, label]) => label),
      tickmarkPlacement: "on",
      lineWidth: 0,
      labels: { style: { fontSize: "11px" } },
    },
    yAxis: {
      gridLineInterpolation: "polygon",
      min: 0,
      max: 6,
      tickInterval: 1,
    },
    tooltip: {
      shared: true,
      pointFormat:
        '<span style="color:{series.color}">●</span> {series.name}: <b>{point.y}</b><br/>',
    },
    legend: {
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      itemStyle: { fontSize: "11px" },
    },
    series: series.map((serie) => ({
      name: serie.name,
      data: serie.data,
      pointPlacement: "on",
      color: serie.color,
    })),
  });

  const rawPolarSeries = (distributions?.polar?.series || []).filter(
    (series) =>
      Array.isArray(series.data) && series.data.some((value) => value !== null),
  );
  const polarSeries = rawPolarSeries.map((series) => {
    const cleanName = normalizeTypologyName(series.name);
    return {
      ...series,
      name: cleanName,
      originalName: series.name,
      color: typologyColorMap[cleanName] || "#2563eb",
    };
  });
  const withIncentiveSeries = polarSeries.filter((series) =>
    series.originalName?.startsWith("Com incentivo"),
  );
  const withoutIncentiveSeries = polarSeries.filter((series) =>
    series.originalName?.startsWith("Sem incentivo"),
  );

  const buildTypologyAverageOptions = (series) => ({
    chart: {
      type: "bar",
      height: 260,
      spacing: [8, 8, 8, 8],
      backgroundColor: "transparent",
    },
    title: { text: null },
    credits: { enabled: false },
    exporting: { enabled: false },
    xAxis: {
      categories: series.map((item) => item.name),
      labels: { style: { fontSize: "11px" } },
    },
    yAxis: {
      min: 0,
      max: 6,
      tickInterval: 1,
      title: { text: null },
      plotLines: [
        ...(distributions?.global_average !== null &&
        distributions?.global_average !== undefined
          ? [
              {
                value: distributions.global_average,
                color: "#0f172a",
                width: 1,
                dashStyle: "ShortDash",
                label: {
                  text: "Média global",
                  align: "right",
                  style: { fontSize: "10px" },
                },
              },
            ]
          : []),
        ...(distributions?.year_average !== null &&
        distributions?.year_average !== undefined
          ? [
              {
                value: distributions.year_average,
                color: "#64748b",
                width: 1,
                dashStyle: "ShortDot",
                label: {
                  text: "Média do ano",
                  align: "right",
                  style: { fontSize: "10px" },
                },
              },
            ]
          : []),
      ],
    },
    tooltip: {
      pointFormat: "<b>{point.y}</b>",
    },
    legend: { enabled: false },
    plotOptions: {
      series: {
        dataLabels: {
          enabled: true,
          format: "{y:.2f}",
          style: { textOutline: "none", fontSize: "10px" },
        },
      },
    },
    series: [
      {
        name: "Média",
        data: series.map((item) =>
          item.avg !== null && item.avg !== undefined ? item.avg : 0,
        ),
        colorByPoint: true,
        colors: series.map((item) => item.color || "#2563eb"),
      },
    ],
  });

  return (
    <>
      <ToastNotification toast={toast} setToast={setToast} />
      <CRow className="g-3 mb-4">
        <CCol sm={6} lg={3}>
          <CCard className="surveillance-kpi kpi-total h-100 shadow-sm">
            <CCardBody className="py-3">
              <div className="text-uppercase text-medium-emphasis small">
                Ano
              </div>
              <div className="fs-4 fw-semibold">
                {kpis?.year ?? selectedYear}
              </div>
              <div className="text-medium-emphasis small">
                Período selecionado
              </div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol sm={6} lg={3}>
          <CCard className="surveillance-kpi kpi-completion h-100 shadow-sm">
            <CCardBody className="py-3">
              <div className="text-uppercase text-medium-emphasis small">
                Nº de clientes
              </div>
              <div className="fs-4 fw-semibold">{kpis?.clients ?? 0}</div>
              <div className="text-medium-emphasis small">Total registado</div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol sm={6} lg={3}>
          <CCard className="surveillance-kpi kpi-pending h-100 shadow-sm">
            <CCardBody className="py-3">
              <div className="text-uppercase text-medium-emphasis small">
                Taxa de resposta
              </div>
              <div className="fs-4 fw-semibold">
                {kpis?.response_rate ?? 0}%
              </div>
              <div className="text-medium-emphasis small">
                {kpis?.concluded ?? 0} preenchidos
              </div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol sm={6} lg={3}>
          <CCard className="surveillance-kpi kpi-overdue h-100 shadow-sm">
            <CCardBody className="py-3">
              <div className="text-uppercase text-medium-emphasis small">
                Emails enviados
              </div>
              <div className="fs-4 fw-semibold">{kpis?.emails_sent ?? 0}</div>
              <div className="text-medium-emphasis small">Total no ano</div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CRow className="g-3 mb-4">
        <CCol lg={4}>
          <CCard className="h-100 shadow-sm">
            <CCardBody className="py-3">
              <div className="text-uppercase text-medium-emphasis small mb-2">
                Médias de pontuação
              </div>
              <CRow className="g-3">
                <CCol sm={6}>
                  <div className="border rounded-3 px-3 py-2 bg-body-tertiary h-100">
                    <div className="text-medium-emphasis small">
                      Média global
                    </div>
                    <div className="fs-4 fw-semibold">
                      {distributions?.global_average ?? "-"}
                    </div>
                  </div>
                </CCol>
                <CCol sm={6}>
                  <div className="border rounded-3 px-3 py-2 bg-body-tertiary h-100">
                    <div className="text-medium-emphasis small">
                      Média de {selectedYear}
                    </div>
                    <div className="fs-4 fw-semibold">
                      {distributions?.year_average ?? "-"}
                    </div>
                  </div>
                </CCol>
              </CRow>
              {withIncentiveSeries.length || withoutIncentiveSeries.length ? (
                <div className="mt-3">
                  <div className="text-uppercase text-medium-emphasis small mb-2">
                    Média por tipologia
                  </div>
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={buildTypologyAverageOptions([
                      ...withIncentiveSeries,
                      ...withoutIncentiveSeries,
                    ])}
                  />
                </div>
              ) : null}
            </CCardBody>
          </CCard>
        </CCol>
        <CCol lg={8}>
          <CCard className="h-100 shadow-sm">
            <CCardBody className="py-3">
              <div className="text-uppercase text-medium-emphasis small mb-2">
                Médias por questão e tipologia ({selectedYear})
              </div>
              <CTabs
                activeItemKey={activePolarTab}
                onChange={setActivePolarTab}
              >
                <CTabList variant="tabs">
                  <CTab itemKey="with">Com incentivo</CTab>
                  <CTab itemKey="without">Sem incentivo</CTab>
                </CTabList>
                <CTabContent>
                  <CTabPanel className="pt-3" itemKey="with">
                    {withIncentiveSeries.length ? (
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={buildPolarOptions(withIncentiveSeries)}
                      />
                    ) : (
                      <div className="text-medium-emphasis small">
                        Sem dados no período selecionado.
                      </div>
                    )}
                  </CTabPanel>
                  <CTabPanel className="pt-3" itemKey="without">
                    {withoutIncentiveSeries.length ? (
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={buildPolarOptions(withoutIncentiveSeries)}
                      />
                    ) : (
                      <div className="text-medium-emphasis small">
                        Sem dados no período selecionado.
                      </div>
                    )}
                  </CTabPanel>
                </CTabContent>
              </CTabs>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CCard className="shadow-sm mb-3">
        <CCardBody className="py-3">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div className="text-uppercase text-medium-emphasis small">
              Filtros
            </div>
            <div className="d-flex align-items-center gap-3">
              <span className="text-medium-emphasis small">
                A mostrar <strong>{tableSurveys.length}</strong> de{" "}
                {kpis?.total_surveys ?? tableSurveys.length} questionários
              </span>
              {(selectedTypologies.length > 0 || onlyConcluded) && (
                <CButton
                  color="link"
                  size="sm"
                  className="p-0"
                  onClick={() => {
                    setSelectedTypologies([]);
                    setMatchAllTypologies(false);
                    setOnlyConcluded(false);
                  }}
                >
                  Limpar filtros
                </CButton>
              )}
            </div>
          </div>
          <CRow className="g-3">
            <CCol lg>
              {typologyGroups.map((group) => (
                <div
                  key={group.label}
                  className="d-flex flex-wrap align-items-center gap-2 mb-2"
                >
                  <span className="small fw-semibold" style={{ minWidth: 110 }}>
                    {group.label}:
                  </span>
                  {group.options.map((option) => {
                    const active = selectedTypologies.includes(option.value);
                    const color = typologyColorMap[option.label] || "#2563eb";
                    return (
                      <CButton
                        key={option.value}
                        size="sm"
                        shape="rounded-pill"
                        aria-pressed={active}
                        style={{
                          border: `1px solid ${color}`,
                          backgroundColor: active ? color : "transparent",
                          color: active ? "#fff" : color,
                        }}
                        onClick={() => toggleTypology(option.value)}
                      >
                        {option.label}
                      </CButton>
                    );
                  })}
                </div>
              ))}
            </CCol>
            <CCol lg="auto" className="satisfaction-filter-options">
              <div className="text-uppercase text-medium-emphasis small mb-2">
                Opções
              </div>
              <CFormSwitch
                id="match-all-typologies"
                className="mb-2 small"
                label="Apenas questionários com todas as tipologias selecionadas"
                checked={matchAllTypologies}
                disabled={selectedTypologies.length < 2}
                onChange={(e) => setMatchAllTypologies(e.target.checked)}
              />
              <CFormSwitch
                id="only-concluded"
                className="mb-0 small"
                label="Apenas questionários preenchidos"
                checked={onlyConcluded}
                onChange={(e) => setOnlyConcluded(e.target.checked)}
              />
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>
      <CCard>
        <CCardHeader className="d-flex justify-content-between align-items-center">
          <h3 className="mb-0">Questionários de Satisfação Anual</h3>

          {is_director && (
            <CButton
              color="info"
              className="text-white"
              onClick={handleSendEmails}
              disabled={loading}
            >
              {loading ? <CSpinner size="sm" /> : "Enviar Questionários"}
            </CButton>
          )}
        </CCardHeader>

        <CCardBody>
          <DataTableComponent
            columns={columns}
            data={tableSurveys}
            years={availableYears}
            selectedYear={selectedYear}
            onYearChange={(newYear) => setSelectedYear(newYear)}
            onRowClicked={setPreviewSurvey}
          />
        </CCardBody>
      </CCard>

      <CCard className="mt-3 shadow-sm">
        <CCardHeader
          role="button"
          className="d-flex justify-content-between align-items-center"
          onClick={() => setCommentsVisible((prev) => !prev)}
          aria-expanded={commentsVisible}
        >
          <h3 className="mb-0">
            Comentários / observações ({surveysWithComments.length})
          </h3>
          <span className="text-medium-emphasis small">
            {commentsVisible ? "Esconder" : "Mostrar"}
          </span>
        </CCardHeader>
        <CCollapse visible={commentsVisible}>
          <CCardBody>
            {surveysWithComments.length ? (
              <div className="d-flex flex-column gap-3">
                {surveysWithComments.map((survey) => (
                  <div
                    key={survey.id}
                    className="border rounded-3 p-3 bg-body-tertiary"
                  >
                    <div className="fw-semibold">{getClientName(survey)}</div>
                    <div
                      className="text-medium-emphasis mt-1"
                      style={{ whiteSpace: "pre-line" }}
                    >
                      {survey.suggestions}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-medium-emphasis small">
                Sem comentários no período selecionado.
              </div>
            )}
          </CCardBody>
        </CCollapse>
      </CCard>

      <RecordDetailOffcanvas
        record={previewSurvey}
        onHide={() => setPreviewSurvey(null)}
        title="Questionário de Satisfação"
        subtitle={previewSurvey ? (previewSurvey.client ? previewSurvey.client_name : previewSurvey.old_client_name) : null}
        fields={previewSurvey ? [
          { label: "Cliente", value: previewSurvey.client ? previewSurvey.client_name : previewSurvey.old_client_name },
          { label: "Preenchido", value: previewSurvey.concluded ? "Sim" : "Não" },
          { label: "Última data de envio", value: previewSurvey.last_sent_date || "" },
          { label: "Qtd. envios", value: previewSurvey.times_sent },
          { label: "Enviado para", value: previewSurvey.sent_to || "" },
          {
            label: "Comentários / observações",
            plain: false,
            value: previewSurvey.suggestions?.trim() ? (
              <div style={{ whiteSpace: "pre-line" }}>
                {previewSurvey.suggestions}
              </div>
            ) : (
              "-"
            ),
          },
        ] : []}
        headerActions={previewSurvey && previewSurvey.concluded && (
          <CButton color="info" variant="outline" size="sm" onClick={() => navigate(`/questionarios-satisfacao/${previewSurvey.id}`)}>
            Ver
          </CButton>
        )}
      />

      {missingEmails && (
        <CModal visible={true} onClose={() => setMissingEmails(null)}>
          <CModalHeader>
            <CModalTitle>
              Empresas sem email para envio de questionários
            </CModalTitle>
          </CModalHeader>
          <CModalBody>
            <ul>
              {missingEmails.map((company, idx) => (
                <li key={idx}>{company}</li>
              ))}
            </ul>
          </CModalBody>
          <CModalFooter>
            <CButton color="secondary" onClick={() => setMissingEmails(null)}>
              Fechar
            </CButton>
          </CModalFooter>
        </CModal>
      )}
    </>
  );
};

export default SatisfactionSurveyList;
